import React, { useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { API_BASE_URL } from "../../config";

const Instructions: React.FC = () => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    // Check if candidateId exists in localStorage
    if (!localStorage.getItem("candidateId")) {
      alert("You must register first before accessing this page.");
      window.location.href = "/registration";
    }
  }, []);

  const handleSubmit = () => {
    if (!isChecked) {
      alert("Please agree to the terms first.");
      return;
    }

    setIsSubmitting(true);
    const candidateId = localStorage.getItem("candidateId");

    // Send an empty response to the endpoint
    fetch(`${API_BASE_URL}/screening-questions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ candidateId }),
    })
      .then(async (response) => {
        if (response.ok) {
          const result = await response.json();
          localStorage.setItem("questionId", result.id);
          window.location.href = "/logical";
        } else {
          alert("Failed to send data. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        alert("An error occurred. Please try again.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Add this useEffect at the top of your other useEffects
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex bg-gradient-to-br justify-center p-4 from-red-50 items-center min-h-screen to-red-200">
      <div className="bg-white rounded-2xl shadow-lg w-full duration-300 hover:shadow-xl max-w-[800px] overflow-hidden transition-all">
        {/* Header */}
        <div className="bg-gradient-to-r p-6 text-center text-white from-red-500 relative to-red-400">
          <h2 className="text-2xl font-bold relative sm:text-3xl z-10">
            Instructions for the Online Test
          </h2>
          <p className="text-sm mt-1 opacity-90 relative sm:text-base z-10">
            Please read the instructions carefully before starting the test
          </p>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 space-y-6">
          <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg">
            <h3 className="text-blue-800 text-lg font-semibold mb-2">
              Test Structure
            </h3>
            <p className="font-medium mb-2">📌 Total Marks: 90</p>
            <p className="mb-2">
              The test is divided into <strong>three main sections</strong>:
            </p>
            <ul className="space-y-1 mb-4">
              <li>
                ✔ <strong>Logical Understanding</strong> – 37 Marks
              </li>
              <li>
                ✔ <strong>Memorization Ability</strong> – 35 Marks
              </li>
              <li>
                ✔ <strong>Aptitude Test</strong> – 18 Marks
              </li>
            </ul>

            <h4 className="font-bold mt-4">
              1. Logical Understanding (37 Marks)
            </h4>
            <ul className="list-disc pl-5 space-y-1 mb-3">
              <li>
                <strong>Part A: Number & Pattern Recognition</strong> – 10
                Questions (10 Marks)
              </li>
              <li>
                <strong>Part B: Logical Reasoning</strong> – 15 Questions (17
                Marks)
              </li>
              <li>
                <strong>Part C: Short Passage & Questions</strong> – 10
                Questions (10 Marks)
              </li>
            </ul>

            <h4 className="font-bold mt-4">
              2. Memorization Ability (35 Marks)
            </h4>
            <ul className="list-disc pl-5 space-y-1 mb-3">
              <li>
                <strong>Part A: Word Recall Challenge</strong> – 15 Questions
                (15 Marks)
              </li>
              <li>
                <strong>Part B: Fill in the Blanks</strong> – 10 Questions (10
                Marks)
              </li>
              <li>
                <strong>Part C: Memory Matching Game</strong> – 5 Questions (5
                Marks)
              </li>
              <li>
                <strong>Part D: Short Story Memory Test</strong> – 5 Questions
                (5 Marks)
              </li>
            </ul>

            <h4 className="font-bold mt-4">3. Aptitude Test (18 Marks)</h4>
            <ul className="list-disc pl-5 space-y-1 mb-3">
              <li>
                <strong>Test 1: Calculation</strong> – 2 Questions (2 Marks)
              </li>
              <li>
                <strong>Test 2: Matching the Blocks</strong> – 3 Questions (2
                Marks)
              </li>
              <li>
                <strong>Test 3: IQ Quiz</strong> – 3 Questions (2 Marks)
              </li>
              <li>
                <strong>Test 4: Japanese Language</strong> – 10 Questions (10
                Marks)
              </li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-100 p-4 rounded-lg">
            <h3 className="text-green-800 text-lg font-semibold mb-3">
              4. General Rules
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✔</span>
                <span>Answer all questions carefully.</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✔</span>
                <span>
                  Do not refresh, leave the page, or open new tabs/windows
                  during the test.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✔</span>
                <span>There is no negative marking.</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✔</span>
                <span>
                  Ensure a stable internet connection before starting.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✔</span>
                <span>
                  You cannot go back to previous questions once answered.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">🆘</span>
                <span>
                  <strong>
                    In case of technical issues, contact support immediately.
                  </strong>
                </span>
              </li>
            </ul>

            <div className="mt-4 ">
              <p className="font-medium">
                📞 <strong>Support Contact:</strong>
              </p>
              <ul className="ml-6">
                <li>+91 9071001353 (Harshitha San)</li>
                <li>+91 9071002349 (Swaroop San)</li>
              </ul>
              <p className="mt-2 font-medium">
                📌 <strong>Note:</strong> Make a{" "}
                <strong>note of this number now</strong>, as you{" "}
                <strong>
                  won't be able to return to this page once the test starts.
                </strong>
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-100 p-4 rounded-lg">
            <h3 className="text-amber-800 text-lg font-semibold mb-3">
              5. Time Limit
            </h3>
            <p className="mb-2">
              <span className="text-amber-500 mr-2">⏳</span>{" "}
              <strong>Total duration: 40 minutes.</strong> Complete the test and
              click <strong>Submit</strong>. If time runs out, your answers will
              be <strong>saved automatically.</strong>
            </p>
            <p>
              <span className="text-green-500 mr-2">✅</span> Once you{" "}
              <strong>tick the checkbox</strong> confirming that you have read
              the <strong>Terms & Conditions</strong>, the{" "}
              <strong>test will start automatically</strong>, and the{" "}
              <strong>timer will begin</strong>.
            </p>
            <p className="mt-4 text-center font-bold">All the best! 🎯🔥</p>
          </div>

          {/* Checkbox Agreement */}
          <div className="flex bg-gray-50 border border-gray-200 p-4 rounded-lg items-start space-x-3">
            <div className="flex h-5 items-center">
              <input
                id="agreement"
                type="checkbox"
                className="h-5 rounded w-5 accent-red-500 focus:ring-2 focus:ring-red-300"
                required
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
              />
            </div>
            <label
              htmlFor="agreement"
              className="text-gray-700 text-sm font-medium"
            >
              I have read and understood all the instructions above and agree to
              comply with them during the test.
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-between space-x-4">
            <button
              disabled={!isChecked || isSubmitting}
              onClick={handleSubmit}
              className={`flex-1 bg-gradient-to-r rounded-lg text-center text-white font-medium from-green-500 px-4 py-3 to-green-400 transition ${
                isChecked && !isSubmitting
                  ? "hover:shadow-lg duration-200"
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              {isSubmitting ? (
                <ThreeDots
                  height="24"
                  width="50"
                  color="#ffffff"
                  ariaLabel="loading"
                  wrapperClass="flex justify-center items-center"
                />
              ) : (
                <>
                  I Understand and Start Test <span className="ml-2">✓</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instructions;
